// utils/decrypt.js
//
// Decrypts AES-256-GCM payloads produced by the Django backend's `encrypt_data()`.
// Works in all environments: HTTP IP addresses, HTTPS domains, Node.js, and browser contexts.

import { gcm } from "@noble/ciphers/aes.js";

const KEY_B64 =
  (typeof process !== "undefined" &&
    process.env?.REACT_APP_ENCRYPTION_KEY) ||
  "fwROkLXFKXoXr69CwrBOAsujOInxYJCbsM4bfrCuxQs=";

function base64ToBytes(b64) {
  const cleaned = String(b64).trim().replace(/\s/g, "");

  if (cleaned.length === 0) {
    throw new Error("base64ToBytes: received an empty string");
  }
  if (cleaned.length % 4 !== 0) {
    throw new Error(
      `base64ToBytes: invalid base64 length (${cleaned.length}), likely truncated or corrupted`,
    );
  }

  return Uint8Array.from(atob(cleaned), (c) => c.charCodeAt(0));
}

export async function decryptPayload(payloadB64) {
  const bytes = base64ToBytes(payloadB64);
  const iv = bytes.slice(0, 12);
  const ciphertextAndTag = bytes.slice(12);
  const rawKey = base64ToBytes(KEY_B64);

  // 1. Try Native Web Crypto API if available (in Secure Contexts / HTTPS / localhost)
  if (typeof window !== "undefined" && window.crypto?.subtle) {
    try {
      const cryptoKey = await window.crypto.subtle.importKey(
        "raw",
        rawKey,
        { name: "AES-GCM" },
        false,
        ["decrypt"],
      );
      const decrypted = await window.crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        cryptoKey,
        ciphertextAndTag,
      );
      const text = new TextDecoder().decode(decrypted);
      return JSON.parse(text);
    } catch (subtleErr) {
      console.warn(
        "[decrypt.js] Web Crypto failed, using pure JS fallback:",
        subtleErr,
      );
    }
  }

  // 2. Pure JavaScript AES-256-GCM Decryption (Works on plain HTTP IP addresses without HTTPS/domain!)
  const cipher = gcm(rawKey, iv);
  const decryptedBytes = cipher.decrypt(ciphertextAndTag);
  const text = new TextDecoder().decode(decryptedBytes);
  return JSON.parse(text);
}

export async function mergeDecrypted(raw) {
  if (!raw) return raw;
  const { secure, ...rest } = raw;

  const isMissing =
    !secure ||
    typeof secure !== "string" ||
    secure.trim().length === 0 ||
    secure === "undefined" ||
    secure === "null";

  if (isMissing) {
    return rest;
  }

  try {
    const decrypted = await decryptPayload(secure);
    return { ...rest, ...decrypted };
  } catch (err) {
    console.error("Failed to decrypt secure payload:", err);
    return rest;
  }
}
